import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FunctionsIcon from '@mui/icons-material/Functions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { AGGREGATE_FUNCTIONS, TIME_PERIOD_AGGREGATIONS } from '../../constants';
import { isSimpleAggregateFormula, getMetricPrimarySortColumn } from '../../utils';
import type { OrderByDirection } from '../../types';

const SIMPLE_AGG_EXTRACT = /^(SUM|AVG|COUNT|MIN|MAX|MEDIAN|STDDEV|VARIANCE)\s*\(/i;

export default function MetricConfigPopover({
  metricName,
  instanceId,
  formula,
  dateDimensions,
}: {
  metricName: string;
  instanceId: string;
  formula: string;
  dateDimensions: { name: string; column: string; type: string; description: string }[];
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const metricConfigs = useVoyagerStore((s) => s.metricConfigs);
  const setMetricAlias = useVoyagerStore((s) => s.setMetricAlias);
  const setMetricAggregationOverride = useVoyagerStore((s) => s.setMetricAggregationOverride);
  const setMetricTimePeriod = useVoyagerStore((s) => s.setMetricTimePeriod);
  const setMetricDateDimension = useVoyagerStore((s) => s.setMetricDateDimension);
  const orderBy = useVoyagerStore((s) => s.orderBy);
  const setOrderByDirection = useVoyagerStore((s) => s.setOrderByDirection);

  const config = metricConfigs[instanceId];
  const isSimple = isSimpleAggregateFormula(formula);
  const defaultAggMatch = formula.match(SIMPLE_AGG_EXTRACT);
  const defaultAgg = defaultAggMatch ? defaultAggMatch[1].toUpperCase() : 'SUM';

  const currentAgg = config?.aggregationOverride || defaultAgg;
  const currentTimePeriod = config?.timePeriod || '';
  const currentDateDim = config?.dateDimension || '';
  const currentAlias = config?.alias || '';

  const sortColumn = getMetricPrimarySortColumn(metricName, config);
  const currentSort = orderBy.find((o) => o.column === sortColumn)?.direction ?? null;

  const activeCount = [
    config?.aggregationOverride,
    config?.timePeriod,
    config?.alias,
    currentSort,
  ].filter(Boolean).length;

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Metric settings">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            p: 0.25,
            color: activeCount > 0 ? 'primary.main' : 'text.secondary',
          }}
        >
          <Badge
            badgeContent={activeCount}
            color="primary"
            variant="dot"
            invisible={activeCount === 0}
          >
            <FunctionsIcon sx={{ fontSize: 18 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Configure: {metricName}
          </Typography>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
              Aggregation
            </Typography>
            <Tooltip title={!isSimple ? 'Complex formula — override not supported' : ''} placement="right">
              <span>
                <FormControl size="small" variant="outlined" fullWidth>
                  <Select
                    value={currentAgg}
                    disabled={!isSimple}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMetricAggregationOverride(instanceId, val === defaultAgg ? undefined : val);
                    }}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {AGGREGATE_FUNCTIONS.map((fn) => (
                      <MenuItem key={fn.value} value={fn.value} sx={{ fontSize: '0.8rem' }}>{fn.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </span>
            </Tooltip>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
              Time Period
            </Typography>
            <FormControl size="small" variant="outlined" fullWidth>
              <Select
                value={currentTimePeriod}
                displayEmpty
                onChange={(e) => setMetricTimePeriod(instanceId, e.target.value || undefined)}
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>None</em></MenuItem>
                {TIME_PERIOD_AGGREGATIONS.map((tp) => (
                  <MenuItem key={tp.value} value={tp.value} sx={{ fontSize: '0.8rem' }}>{tp.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {currentTimePeriod && dateDimensions.length > 1 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Date Dimension
              </Typography>
              <FormControl size="small" variant="outlined" fullWidth>
                <Select
                  value={currentDateDim}
                  displayEmpty
                  onChange={(e) => setMetricDateDimension(instanceId, e.target.value || undefined)}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>Default</em></MenuItem>
                  {dateDimensions.map((dd) => (
                    <MenuItem key={dd.name} value={dd.name} sx={{ fontSize: '0.8rem' }}>{dd.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
              Output Alias
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Custom column name"
              value={currentAlias}
              onChange={(e) => setMetricAlias(instanceId, e.target.value)}
              sx={{ '& input': { fontSize: '0.8rem' } }}
            />
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
              Sort
              {currentTimePeriod && TIME_PERIOD_AGGREGATIONS.find(tp => tp.value === currentTimePeriod && ['MoM','QoQ','YoY'].includes(tp.value)) && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled', fontSize: '0.7rem' }}>
                  (on {sortColumn})
                </Typography>
              )}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={currentSort ?? 'none'}
              onChange={(_, val: string | null) => {
                if (val === null || val === currentSort || val === 'none') {
                  setOrderByDirection(sortColumn, null);
                } else {
                  setOrderByDirection(sortColumn, val as OrderByDirection);
                }
              }}
              fullWidth
              sx={{ '& .MuiToggleButton-root': { fontSize: '0.7rem', py: 0.25 } }}
            >
              <ToggleButton value="none">Default</ToggleButton>
              <ToggleButton value="ASC">Asc ↑</ToggleButton>
              <ToggleButton value="DESC">Desc ↓</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
